import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "./ui/use-toast";
import { Upload, X, Loader2, ChevronRight, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { isCategoryEnabled } from "@/lib/cookieConsent";

const formSchema = z.object({
  primaryService: z.string().min(1, "Please select a service"),
  message: z.string().max(600, "Message must be 600 characters or less").optional(),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  phone: z.string().min(10, "Phone number is required"),
  email: z.string().email("Valid email is required"),
  boatName: z.string().optional(),
  boatLocation: z.string().min(1, "Boat location is required"),
  vesselLength: z.string().optional(),
  hearAbout: z.string().min(1, "Please tell us how you heard about us"),
  consent: z.boolean().refine(val => val === true, "You must agree to continue"),
  honeypot: z.string().max(0),
});

type FormData = z.infer<typeof formSchema>;

const services = [
  "Glass Window/Hatch Installation or Replacement",
  "Acrylic Window/Hatch Installation or Replacement",
  "Polishing and Scratch Removal",
  "Leak Repairs",
  "Custom Fabrication",
  "Resealing and Re-bedding",
  "Marine Window Tinting",
  "Inspection and Consultation",
  "Other",
];

const hearAboutOptions = [
  "Google Search",
  "Google Business Profile",
  "Instagram",
  "Facebook",
  "Referral from Friend/Family",
  "Referral from Marina",
  "Boat Show",
  "Other",
];

export const LeadForm = ({ isMobileOverlay = false, onClose }: { isMobileOverlay?: boolean; onClose?: () => void }) => {
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    setValue,
    watch,
    trigger,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
    defaultValues: {
      honeypot: "",
      consent: false,
    },
  });

  const watchedFields = watch();
  const hasFilledFields = Object.values(watchedFields).some(val => val !== false && val !== "" && val !== undefined && val !== null);

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      return !match[2] ? match[1] : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ""}`;
    }
    return value;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validFiles = selectedFiles.filter(file => {
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      const maxSize = 10 * 1024 * 1024; // 10MB
      return validTypes.includes(file.type) && file.size <= maxSize;
    });

    if (validFiles.length + files.length > 3) {
      toast({
        title: "Too many files",
        description: "You can upload maximum 3 images",
        variant: "destructive",
      });
      return;
    }

    setFiles([...files, ...validFiles].slice(0, 3));
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const goToStep2 = async () => {
    const isValid = await trigger(["primaryService", "message"]);
    if (isValid) {
      setStep(2);
    }
  };

  const onSubmit = async (data: FormData) => {
    if (data.honeypot) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload files if any.
      // We store the storage *path* (e.g. `temp/<uuid>.jpg`) rather than a
      // public URL — the `lead-attachments` bucket is private, so admins
      // and the Zapier webhook mint short-lived signed URLs on demand.
      const filePaths: string[] = [];
      if (files.length > 0) {
        for (const file of files) {
          const fileExt = (file.name.split(".").pop() || "bin").toLowerCase();
          const safeId =
            typeof crypto !== "undefined" && "randomUUID" in crypto
              ? crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          const fileName = `temp/${safeId}.${fileExt}`;
          const { error: uploadError } = await supabase.storage
            .from("lead-attachments")
            .upload(fileName, file, {
              contentType: file.type,
              upsert: false,
            });

          if (uploadError) throw uploadError;

          filePaths.push(fileName);
        }
      }

      // Submit lead via RPC
      const { data: leadId, error } = await supabase
        // @ts-ignore - Supabase types regenerating
        .rpc("submit_lead", {
        p_first_name: data.firstName,
        p_last_name: data.lastName,
        p_phone: data.phone.replace(/\D/g, ""),
        p_email: data.email,
        p_primary_service: data.primaryService,
        p_boat_location: data.boatLocation,
        p_hear_about: data.hearAbout,
        p_boat_name: data.boatName || null,
        p_vessel_length: data.vesselLength ? parseFloat(data.vesselLength) : null,
        p_message: data.message || null,
        p_file_urls: filePaths.length > 0 ? JSON.stringify(filePaths) : null,
        p_ip: null,
        p_ua: navigator.userAgent,
      });

      if (error) throw error;

      // Track conversion (if consent given)
      if (isCategoryEnabled('analytics') && typeof window.gtag !== "undefined") {
        window.gtag("event", "generate_lead", {
          event_category: "Form",
          event_label: data.primaryService,
        });
      }

      // Track Meta Pixel (if consent given and available)
      if (isCategoryEnabled('marketing') && typeof window.fbq !== "undefined") {
        window.fbq('track', 'Lead', {
          content_name: 'Marine Window Service Request',
          content_category: data.primaryService,
        });
      }

      navigate("/thank-you?ref=quote");
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission failed",
        description: "Please try again or call us directly.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (hasFilledFields && isMobileOverlay) {
      if (window.confirm("You have unsaved changes. Are you sure you want to close?")) {
        onClose?.();
      }
    } else {
      onClose?.();
    }
  };

  return (
    <div className={`relative ${isMobileOverlay ? "p-6" : ""}`}>
      {isMobileOverlay && (
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl mx-auto space-y-6">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className={`h-2 w-2 rounded-full ${step === 1 ? "bg-azure-500" : "bg-gray-300"}`} />
          <div className={`h-2 w-2 rounded-full ${step === 2 ? "bg-azure-500" : "bg-gray-300"}`} />
          <span className="ml-2 text-sm text-muted-foreground">Step {step} of 2</span>
        </div>

        {/* Honeypot */}
        <input
          type="text"
          {...register("honeypot")}
          style={{ position: "absolute", left: "-9999px", opacity: 0 }}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />

        {step === 1 && (
          <div className="space-y-6">
            <div>
              <Label htmlFor="primaryService">Primary Service Needed *</Label>
              <Select
                onValueChange={(value) => setValue("primaryService", value)}
                defaultValue={watchedFields.primaryService}
              >
                <SelectTrigger id="primaryService" aria-label="Primary Service Needed">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service} value={service}>
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.primaryService && (
                <p className="text-sm text-destructive mt-1">{errors.primaryService.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="message">Message (optional)</Label>
              <Textarea
                id="message"
                {...register("message")}
                placeholder="Tell us about your project..."
                maxLength={600}
                rows={4}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {watchedFields.message?.length || 0}/600 characters
              </p>
              {errors.message && (
                <p className="text-sm text-destructive mt-1">{errors.message.message}</p>
              )}
            </div>

            <div>
              <Label>File Upload (optional)</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Upload up to 3 images (JPEG, PNG, WebP, max 10MB each)
              </p>
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  disabled={files.length >= 3}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm">Click to upload or drag and drop</p>
                </label>
              </div>
              
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded">
                      <span className="text-sm flex-1 truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-destructive hover:text-destructive/80"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              type="button"
              onClick={goToStep2}
              disabled={!watchedFields.primaryService}
              className="w-full bg-azure-500 hover:bg-azure-500/90"
            >
              Continue
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  {...register("firstName")}
                  placeholder="John"
                />
                {errors.firstName && touchedFields.firstName && (
                  <p className="text-sm text-destructive mt-1">{errors.firstName.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  {...register("lastName")}
                  placeholder="Doe"
                />
                {errors.lastName && touchedFields.lastName && (
                  <p className="text-sm text-destructive mt-1">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                {...register("phone")}
                placeholder="(619) 555-1234"
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  setValue("phone", formatted);
                }}
              />
              {errors.phone && touchedFields.phone && (
                <p className="text-sm text-destructive mt-1">{errors.phone.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                {...register("email")}
                placeholder="john@example.com"
              />
              {errors.email && touchedFields.email && (
                <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="boatName">Boat/Yacht Name (optional)</Label>
              <Input
                id="boatName"
                {...register("boatName")}
                placeholder="Sea Breeze"
              />
            </div>

            <div>
              <Label htmlFor="boatLocation">Boat/Yacht Location *</Label>
              <Input
                id="boatLocation"
                {...register("boatLocation")}
                placeholder="San Diego Bay, Safe Harbor Sunroad Marina"
              />
              {errors.boatLocation && touchedFields.boatLocation && (
                <p className="text-sm text-destructive mt-1">{errors.boatLocation.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="vesselLength">Vessel Length (optional)</Label>
              <Input
                id="vesselLength"
                type="number"
                {...register("vesselLength")}
                placeholder="e.g., 35 feet"
                defaultValue="35"
                min="0"
                step="1"
              />
            </div>

            <div>
              <Label htmlFor="hearAbout">How did you hear about us? *</Label>
              <Select
                onValueChange={(value) => setValue("hearAbout", value)}
                defaultValue={watchedFields.hearAbout}
              >
                <SelectTrigger id="hearAbout" aria-label="How did you hear about us">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  {hearAboutOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.hearAbout && (
                <p className="text-sm text-destructive mt-1">{errors.hearAbout.message}</p>
              )}
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="consent"
                checked={watchedFields.consent}
                onCheckedChange={(checked) => setValue("consent", checked as boolean)}
              />
              <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
                I agree to the{" "}
                <a href="/terms" className="text-azure-500 hover:underline">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="/privacy" className="text-azure-500 hover:underline">
                  Privacy Policy
                </a>
                . *
              </Label>
            </div>
            {errors.consent && (
              <p className="text-sm text-destructive">{errors.consent.message}</p>
            )}

            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1"
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-azure-500 hover:bg-azure-500/90"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Request Quote"
                )}
              </Button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};
